import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Suspense, useContext, useEffect } from "react";
import { AuthContext } from "./context/auth-context";
import { App as CapacitorApp } from "@capacitor/app";
import AppLayout from "./components/app-layout";
import LoadingScreen, { PageSpinner } from "./components/loading-screen";
import { ErrorBoundary } from "./components/error-boundary";
import { lazyWithRetry } from "./lib/lazy-with-retry";
// The first screens users see stay in the main bundle; everything else is
// loaded on demand so the app starts faster.
import LoginPage from "./pages/LoginPage";
import FeedPage from "./pages/FeedPage";
import RoleRoute from "./components/role-route";

const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const ChatPage = lazyWithRetry(() => import("./pages/ChatPage"));
const ExercisesPage = lazyWithRetry(() => import("./pages/ExercisesPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const UserProfile = lazyWithRetry(() => import("./pages/UserProfilePage"));
const SubscrubePaymentPage = lazyWithRetry(() => import("./pages/SubscribePaymentPage"));
const SubscriptionCanceledPage = lazyWithRetry(() => import("./pages/SubscriptionCanceledPage"));
const SubscriptionSuccessPage = lazyWithRetry(() => import("./pages/SubscriptionSuccessPage"));
const SubscribePage = lazyWithRetry(() => import("./pages/SubscribePage"));
const ManageUsersPage = lazyWithRetry(() => import("./pages/UsersPage"));
const AssignPlansPage = lazyWithRetry(() => import("./pages/AssignPlansPage"));
const FoodsPage = lazyWithRetry(() => import("./pages/FoodsPage"));
const SystemLogsPage = lazyWithRetry(() => import("./pages/SystemLogsPage"));
const ActivityPage = lazyWithRetry(() => import("./pages/ActivityPage"));
const ActivateAccountPage = lazyWithRetry(() => import("./pages/ActivateAccountPage"));
const ActivationPage = lazyWithRetry(() => import("./pages/ActivationPage"));

import { useOutlet } from "react-router-dom";

function ProtectedLayout() {
  const auth = useContext(AuthContext);
  const outlet = useOutlet();

  const location = useLocation();

  if (auth?.loading) {
    return <LoadingScreen />;
  }

  if (!auth?.user) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout>
      {/* Keyed by path so a crash on one page doesn't take down navigation. */}
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<PageSpinner />}>{outlet}</Suspense>
      </ErrorBoundary>
    </AppLayout>
  );
}

// Public only route (redirect if logged in)
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const auth = useContext(AuthContext);

  if (auth?.loading) {
    return <LoadingScreen />;
  }

  if (auth?.user) {
    return <Navigate to="/feed" replace />;
  }

  return <>{children}</>;
}

function App() {
  const env: string = import.meta.env.VITE_ENV;
  const navigate = useNavigate();

  useEffect(() => {
    let handler: any;

    const setupBackButton = async () => {
      handler = await CapacitorApp.addListener("backButton", ({ canGoBack }) => {
        // Check if a modal is visibly open by checking pointer-events on body or scroll lock
        const bodyStyles = window.getComputedStyle(document.body);
        const wasModalOpen =
          bodyStyles.pointerEvents === "none" ||
          document.body.hasAttribute("data-scroll-locked");

        // Radix and Vaul popups usually listen to Escape to close. Firing it closes the topmost popup.
        const event = new KeyboardEvent("keydown", {
          key: "Escape",
          bubbles: true,
          cancelable: true,
        });
        document.dispatchEvent(event);

        // If event was default-prevented (Radix did its job) or if a modal was confirmed physically open
        if (event.defaultPrevented || wasModalOpen) {
          return;
        }

        const currentPath = window.location.pathname;

        if (currentPath === "/" || currentPath === "/feed") {
          CapacitorApp.exitApp();
        } else if (canGoBack) {
          navigate(-1);
        } else {
          CapacitorApp.exitApp();
        }
      });
    };

    setupBackButton();

    return () => {
      if (handler && handler.remove) {
        handler.remove();
      }
    };
  }, [navigate]);

  return (
    <div className="min-h-screen safe-area-container">
      {env === "dev" && (
        <div className="fixed left-0 bottom-0 bg-red-500 text-white px-4 py-2 z-50">
          DEV MODE
        </div>
      )}
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public only route - Login page */}
        <Route
          path="/"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/activate-account"
          element={
            <PublicOnlyRoute>
              <ActivateAccountPage />
            </PublicOnlyRoute>
          }
        />

        {/* Public route - Reset Password */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Public route - link from the activation email */}
        <Route path="/aktivacija" element={<ActivationPage />} />

        {/* Protected routes with AppLayout */}
        <Route element={<ProtectedLayout />}>
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/exercises" element={<ExercisesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:userId" element={<UserProfile />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/subscribe/payment" element={<SubscrubePaymentPage />} />
          <Route path="/subscribe/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscribe/canceled" element={<SubscriptionCanceledPage />} />

          <Route
            path="/admin"
            element={
              <RoleRoute allowedRoles={["admin"]}>
                <AdminPage />
              </RoleRoute>
            }
          />

          <Route
            path="/users"
            element={
              <RoleRoute allowedRoles={["admin", "trainer", "glavni_trener"]}>
                <ManageUsersPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/assign-plans"
            element={
              <RoleRoute allowedRoles={["admin", "trainer", "glavni_trener"]}>
                <AssignPlansPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/foods"
            element={
              <RoleRoute allowedRoles={["admin", "trainer", "glavni_trener"]}>
                <FoodsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/logs"
            element={
              <RoleRoute allowedRoles={["admin", "glavni_trener"]}>
                <SystemLogsPage />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <RoleRoute allowedRoles={["admin", "trainer", "glavni_trener"]}>
                <ActivityPage />
              </RoleRoute>
            }
          />
        </Route>

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default App;
